import { ArrowRight, Check } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "../i18n";
import { juhuu } from "../juhuuClass";

// Ändere die maximale Breite der CTA-Sektion
export default function CTASection() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [userMessage, setUserMessage] = useState("");
  const [status, setStatus] = useState<
    "not_sent" | "error" | "send" | "loading"
  >("not_sent");

  const handleSendInquiry = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    const query = await juhuu.settings.sendSupportRequest({
      message:
        "CTA inquiry from " +
        name +
        " (" +
        company +
        "): " +
        userMessage,
    });

    if (query.ok === false) {
      setStatus("error");
    } else {
      setStatus("send");
      setName("");
      setCompany("");
      setUserMessage("");
    }
  };
  return (
    <section className="relative w-full">
      {/* Hintergrund mit Farbverlauf */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-700 via-purple-800 to-navy-900"></div>

      {/* Dekorative Elemente */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-purple-400 rounded-full opacity-10"></div>
        <div className="absolute top-1/2 -left-24 w-64 h-64 bg-blue-400 rounded-full opacity-10"></div>
        <div className="absolute -bottom-32 right-1/3 w-80 h-80 bg-indigo-300 rounded-full opacity-10"></div>
      </div>

      {/* Organischer Übergang zum Footer */}
      <div
        className="absolute bottom-0 left-0 w-full overflow-hidden"
        style={{ height: "150px" }}
      >
        <svg
          className="absolute bottom-0 w-full"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          style={{ height: "150px" }}
        >
          {/* Erste Welle - Hintergrund */}
          <path
            fill="rgba(236, 72, 153, 0.2)"
            d="M0,224L40,213.3C80,203,160,181,240,181.3C320,181,400,203,480,208C560,213,640,203,720,202.7C800,203,880,213,960,229.3C1040,245,1120,267,1200,261.3C1280,256,1360,224,1400,208L1440,192L1440,320L1400,320C1360,320,1280,320,1200,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z"
          ></path>

          {/* Zweite Welle - Mittlere Ebene */}
          <path
            fill="rgba(139, 92, 246, 0.25)"
            d="M0,256L40,261.3C80,267,160,277,240,272C320,267,400,245,480,240C560,235,640,245,720,245.3C800,245,880,235,960,213.3C1040,192,1120,160,1200,165.3C1280,171,1360,213,1400,234.7L1440,256L1440,320L1400,320C1360,320,1280,320,1200,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z"
          ></path>

          {/* Dritte Welle - Vordergrund, die in den Footer übergeht */}
          <path
            fill="url(#gradient-footer)"
            d="M0,288L40,282.7C80,277,160,267,240,250.7C320,235,400,213,480,213.3C560,213,640,235,720,245.3C800,256,880,256,960,234.7C1040,213,1120,171,1200,165.3C1280,160,1360,192,1400,208L1440,224L1440,320L1400,320C1360,320,1280,320,1200,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z"
          ></path>

          <defs>
            <linearGradient
              id="gradient-footer"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#ff0080" stopOpacity="0.7" />
              <stop offset="50%" stopColor="#7928ca" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#0A2540" stopOpacity="0.9" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-20 pb-40">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('components.cta.ready')}
            </h2>
            <p className="text-xl text-purple-100 mb-8">
              {t('components.cta.join')}
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-3">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <p className="text-white">{t('components.cta.launch')}</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-3">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <p className="text-white">{t('components.cta.scale')}</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mr-3">
                  <Check className="h-4 w-4 text-white" />
                </div>
                <p className="text-white">{t('components.cta.pay')}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-2xl border border-white/10">
            <h3 className="text-xl font-bold text-white mb-6">
              {t('components.cta.start')}
            </h3>
            <form className="space-y-4" onSubmit={handleSendInquiry}>
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-purple-100 mb-1"
                >
                  {t('components.cta.fullName')}
                </label>
                <input
                  type="text"
                  id="name"
                  className="w-full px-4 py-3 bg-white/5 border border-purple-400/30 rounded-md text-white placeholder-purple-200/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={t('components.cta.namePlaceholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="block text-sm font-medium text-purple-100 mb-1"
                >
                  {t('components.cta.company')}
                </label>
                <input
                  type="text"
                  id="company"
                  className="w-full px-4 py-3 bg-white/5 border border-purple-400/30 rounded-md text-white placeholder-purple-200/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={t('components.cta.companyPlaceholder')}
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-purple-100 mb-1"
                >
                  {t('components.cta.message')}
                </label>
                <textarea
                  id="message"
                  rows={4}
                  className="w-full px-4 py-3 bg-white/5 border border-purple-400/30 rounded-md text-white placeholder-purple-200/50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={t('components.cta.messagePlaceholder')}
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 transition-colors shadow-lg"
              >
                {status === "loading"
                  ? t('components.cta.wait')
                  : status === "send"
                  ? t('components.cta.sent')
                  : status === "error"
                  ? t('components.cta.error')
                  : t('components.cta.submit')}{" "}
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>

              <p className="text-xs text-purple-200/70 text-center mt-4">
                {t('components.cta.consent')} {" "}
                <a href="/terms" className="underline hover:text-white">
                  {t('components.cta.terms')}
                </a>{" "}
                {t('components.cta.and')}{" "}
                <a href="/privacy" className="underline hover:text-white">
                  {t('components.cta.privacy')}
                </a>
                .
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
